/**
 * Time Utility Functions for Saudi Arabia Timezone (AST - UTC+3)
 */

const SAUDI_TIMEZONE = 'Asia/Riyadh';

/**
 * Get current time in Saudi Arabia timezone
 * @returns {Date} Current date/time in Saudi Arabia
 */
export const getSaudiTime = () => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: SAUDI_TIMEZONE }));
};

/**
 * Convert any date to Saudi Arabia timezone and format it
 * @param {string|Date} date - Date to convert (assumed to be UTC)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in Saudi Arabia timezone
 */
export const toSaudiDate = (date, options = {}) => {
  if (!date) return '';
  
  const defaultOptions = {
    timeZone: SAUDI_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  // Parse the UTC date string and convert to Saudi time
  const utcDate = new Date(date);
  return utcDate.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Convert any date/time to Saudi Arabia timezone and format it
 * @param {string|Date} date - Date to convert (assumed to be UTC)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date-time string in Saudi Arabia timezone
 */
export const toSaudiDateTime = (date, options = {}) => {
  if (!date) return '';
  
  const defaultOptions = {
    timeZone: SAUDI_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  // Parse the UTC date string and convert to Saudi time
  const utcDate = new Date(date);
  return utcDate.toLocaleString('en-US', defaultOptions);
};

/**
 * Convert any time to Saudi Arabia timezone and format it (time only)
 * @param {string|Date} date - Date to convert (assumed to be UTC)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted time string in Saudi Arabia timezone
 */
export const toSaudiTime = (date, options = {}) => {
  if (!date) return '';
  
  const defaultOptions = {
    timeZone: SAUDI_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  // Parse the UTC date string and convert to Saudi time
  const utcDate = new Date(date);
  return utcDate.toLocaleTimeString('en-US', defaultOptions);
};

/**
 * Get relative time in Saudi Arabia timezone (e.g., "2 hours ago", "just now")
 * @param {string|Date} date - Date to convert
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  
  return toSaudiDate(date);
};

/**
 * Format date for Saudi Arabia with Arabic support
 * @param {string|Date} date - Date to convert
 * @param {string} locale - Locale ('en-US' or 'ar-SA')
 * @returns {string} Formatted date string
 */
export const toSaudiDateLocale = (date, locale = 'en-US') => {
  if (!date) return '';
  
  const options = {
    timeZone: SAUDI_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  const utcDate = new Date(date);
  return utcDate.toLocaleDateString(locale, options);
};

/**
 * Get Saudi Arabia date/time for form inputs
 * @returns {Object} { date: 'YYYY-MM-DD', time: 'HH:MM' }
 */
export const getSaudiDateTimeForInput = () => {
  const now = new Date();
  const saudiTimeStr = now.toLocaleString('en-US', { timeZone: SAUDI_TIMEZONE });
  const saudiTime = new Date(saudiTimeStr);
  
  const year = saudiTime.getFullYear();
  const month = String(saudiTime.getMonth() + 1).padStart(2, '0');
  const day = String(saudiTime.getDate()).padStart(2, '0');
  const hours = String(saudiTime.getHours()).padStart(2, '0');
  const minutes = String(saudiTime.getMinutes()).padStart(2, '0');
  
  return { 
    date: `${year}-${month}-${day}`, 
    time: `${hours}:${minutes}` 
  };
};

